import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { executeSoon } from '@streamr/tv-utils';
import { useEffect } from 'react';
import styled from 'styled-components';
import { useToastStacking } from '../../hooks/useToastStacking';
import { Popup } from '../../utils/popups';
import { AbstractPopup } from './AbstractPopup';
function PrivateMessageToast({ senderId, senderDisplayName, messagePreview, onViewClick, reject, resolve, }) {
    const { containerRef, offsetY } = useToastStacking();
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            resolve();
        }, 5000);
        return () => {
            clearTimeout(timeoutId);
        };
    }, [resolve]);
    const handleViewClick = () => {
        onViewClick?.();
        resolve();
    };
    return (_jsx(PrivateMessageToastRoot, { "$offsetY": offsetY, reject: reject, children: _jsxs(ToastContent, { ref: containerRef, children: [_jsx(AvatarContainer, { children: _jsx(Avatar, { children: (senderDisplayName || senderId)
                            .slice(0, 2)
                            .toUpperCase() }) }), _jsxs(ContentSection, { children: [_jsxs(TextContent, { children: [_jsx(SenderName, { children: senderDisplayName || senderId }), _jsx(MessagePreview, { children: messagePreview })] }), _jsx(ViewButton, { type: "button", onClick: handleViewClick, children: "View" })] })] }) }));
}
const PrivateMessageToastRoot = styled(AbstractPopup) `
    --Popup_BorderRadius: 1rem;
    --Popup_Width: auto;
    --Popup_Padding: 0.75rem 1rem;
    --Popup_Duration: 300ms;
    --Popup_BackdropFilter: blur(34px);
    --Popup_Background: rgba(57, 56, 56, 0.6);
    --Popup_Border: 0.0875rem solid rgba(255, 255, 255, 0.4);
    --Popup_BackdropColor: transparent;
    --Popup_InitialBackdropColor: transparent;
    
    /* Position at bottom right above the footer */
    --Popup_Left: 0;
    --Popup_Top: 0;
    --Popup_X: calc(calc(var(--vw, 1vw) * 100) - 100% - 1.5rem);
    --Popup_Y: calc(calc(var(--vh, 1vh) * 100) - 100% - 8rem - ${(props) => props.$offsetY}px);
    --Popup_InitialOffsetY: 0.5rem;
    --Popup_OffsetY: 0;
    
    /* On smaller screens, position slightly higher */
    @media (max-width: 48rem) {
        --Popup_Y: calc(calc(var(--vh, 1vh) * 100) - 100% - 10rem - ${(props) => props.$offsetY}px);
        --Popup_X: calc(calc(var(--vw, 1vw) * 100) - 100% - 1rem);
    }
`;
const ToastContent = styled.div `
    display: flex;
    gap: 1rem;
    align-items: flex-start;
`;
const AvatarContainer = styled.div `
    flex-shrink: 0;
`;
const Avatar = styled.div `
    width: 2rem;
    height: 2rem;
    border-radius: 6.25rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Poppins', sans-serif;
    font-size: 0.75rem;
    font-weight: 600;
    color: #ffffff;
`;
const ContentSection = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    justify-content: center;
    padding-top: 0.1875rem;
    flex: 1;
`;
const TextContent = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    max-width: 18.625rem;
`;
const SenderName = styled.p `
    font-family: 'Poppins', sans-serif;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.375rem;
    color: rgba(255, 255, 255, 0.96);
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;
const MessagePreview = styled.p `
    font-family: 'Poppins', sans-serif;
    font-size: 0.9375rem;
    line-height: 1.375rem;
    color: #ffffff;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;
const ViewButton = styled.button `
    background: rgba(231, 231, 231, 0.2);
    border: none;
    border-radius: 0.375rem;
    height: 1.5rem;
    padding: 0 0.375rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Poppins', sans-serif;
    font-size: 0.8125rem;
    font-weight: 500;
    line-height: 1.25rem;
    color: #ffffff;
    cursor: pointer;
    transition: background-color 200ms;
    align-self: flex-start;

    &:hover {
        background: rgba(231, 231, 231, 0.3);
    }
`;
const privateMessageToast = Popup.create(PrivateMessageToast);
export function showPrivateMessageToast({ senderId, senderDisplayName, message, onViewClick, }) {
    // Truncate message if too long for preview
    const messagePreview = message.length > 50 ? `${message.slice(0, 47)}...` : message;
    executeSoon(async () => {
        try {
            await privateMessageToast.pop({
                senderId,
                senderDisplayName,
                messagePreview,
                onViewClick,
            });
        }
        catch (_) { }
    });
}
